import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Star, ArrowLeft, Save, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/api";
import { Button } from "@/components/ui/button";

const EMPTY = {
  name: "",
  text: "",
  rating: 5,
  cafe: "",
  photo_url: "",
  lang: "ar",
  order: 0,
};

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="p-1"
          data-testid={`rating-star-${n}`}
        >
          <Star
            size={22}
            className={
              n <= value
                ? "fill-[#C86240] text-[#C86240]"
                : "text-[#2A2421]/30"
            }
          />
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-[#5C544F] mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-[#2A2421]/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86240]/40 focus:border-[#C86240]/40";

export default function AdminTestimonials() {
  const { t } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState(null); // null | "new" | id
  const [form, setForm] = React.useState(EMPTY);

  const adminQuery = useQuery({
    queryKey: ["admin", "check"],
    queryFn: () => api.get("/admin/check").then((r) => r.data),
    enabled: !!user,
    retry: false,
    staleTime: 60_000,
  });
  const isAdmin = user ? adminQuery.data?.is_admin === true : false;
  const adminLoading = authLoading || (!!user && adminQuery.isLoading);

  const listQuery = useQuery({
    queryKey: ["testimonials", "all"],
    queryFn: () => api.get("/testimonials").then((r) => r.data),
    enabled: isAdmin,
  });
  const items = listQuery.data || [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["testimonials", "all"] });
  };

  const createMut = useMutation({
    mutationFn: (payload) => api.post("/testimonials", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("✓");
      cancel();
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }) =>
      api.put(`/testimonials/${id}`, payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("✓");
      cancel();
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/testimonials/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("✓");
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const saving = createMut.isPending || updateMut.isPending;

  const startNew = () => {
    setForm({ ...EMPTY, order: items.length + 1 });
    setEditing("new");
  };

  const startEdit = (item) => {
    setForm({
      name: item.name || "",
      text: item.text || "",
      rating: item.rating || 5,
      cafe: item.cafe || "",
      photo_url: item.photo_url || "",
      lang: item.lang || "ar",
      order: item.order || 0,
    });
    setEditing(item.id);
  };

  function cancel() {
    setEditing(null);
    setForm(EMPTY);
  }

  const save = () => {
    if (!form.name.trim() || !form.text.trim()) {
      toast.error(t.admin.name + " / " + t.admin.text);
      return;
    }
    const payload = { ...form, order: Number(form.order) || 0 };
    if (editing === "new") {
      createMut.mutate(payload);
    } else {
      updateMut.mutate({ id: editing, payload });
    }
  };

  const remove = (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    deleteMut.mutate(id);
  };

  if (adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F6F0] text-[#5C544F]">
        {t.common.loading}
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-white rounded-2xl border border-[#2A2421]/10 p-8">
          <div className="w-14 h-14 rounded-full bg-[#C86240]/10 text-[#C86240] flex items-center justify-center mx-auto mb-4">
            <ShieldAlert size={26} />
          </div>
          <h2 className="text-xl font-semibold text-[#2A2421] mb-2">
            {t.admin.title}
          </h2>
          <p className="text-sm text-[#5C544F] mb-6">{t.admin.onlyAdmin}</p>
          <Link to="/">
            <Button className="bg-[#2A2421] text-[#F9F6F0] hover:bg-[#2A2421]/90 rounded-full">
              <ArrowLeft size={14} className="me-1.5" />
              {t.common.back}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="admin-testimonials">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="flex items-center justify-between gap-4 mb-10 flex-wrap">
          <div>
            <Link
              to="/about"
              className="text-xs uppercase tracking-widest text-[#5C544F] hover:text-[#C86240] inline-flex items-center gap-1.5 mb-3"
            >
              <ArrowLeft size={12} />
              {t.common.back}
            </Link>
            <h1 className="text-3xl sm:text-4xl font-display text-[#2A2421]">
              {t.admin.title}
            </h1>
          </div>
          {!editing && (
            <Button
              data-testid="add-testimonial-btn"
              onClick={startNew}
              className="bg-[#C86240] hover:bg-[#C86240]/90 text-white rounded-full"
            >
              <Plus size={16} className="me-1.5" />
              {t.admin.add}
            </Button>
          )}
        </div>

        {editing && (
          <div className="rounded-2xl bg-white border border-[#2A2421]/10 p-6 mb-8 shadow-sm" data-testid="testimonial-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t.admin.name}>
                <input
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="form-name"
                />
              </Field>
              <Field label={t.admin.cafe}>
                <input
                  className={inputCls}
                  value={form.cafe}
                  onChange={(e) => setForm({ ...form, cafe: e.target.value })}
                  data-testid="form-cafe"
                />
              </Field>
              <Field label={t.admin.photo}>
                <input
                  className={inputCls}
                  value={form.photo_url}
                  onChange={(e) =>
                    setForm({ ...form, photo_url: e.target.value })
                  }
                  placeholder="https://…"
                  data-testid="form-photo"
                />
              </Field>
              <Field label={t.admin.lang}>
                <select
                  className={inputCls}
                  value={form.lang}
                  onChange={(e) => setForm({ ...form, lang: e.target.value })}
                  data-testid="form-lang"
                >
                  <option value="ar">{t.admin.langAr}</option>
                  <option value="en">{t.admin.langEn}</option>
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label={t.admin.text}>
                  <textarea
                    className={`${inputCls} min-h-[120px] resize-y`}
                    value={form.text}
                    onChange={(e) =>
                      setForm({ ...form, text: e.target.value })
                    }
                    dir={form.lang === "ar" ? "rtl" : "ltr"}
                    data-testid="form-text"
                  />
                </Field>
              </div>
              <Field label={t.admin.rating}>
                <StarPicker
                  value={form.rating}
                  onChange={(v) => setForm({ ...form, rating: v })}
                />
              </Field>
              <Field label={t.admin.order}>
                <input
                  type="number"
                  className={inputCls}
                  value={form.order}
                  onChange={(e) =>
                    setForm({ ...form, order: e.target.value })
                  }
                  data-testid="form-order"
                />
              </Field>
            </div>
            <div className="flex items-center gap-2 mt-6">
              <Button
                onClick={save}
                disabled={saving}
                className="bg-[#2A2421] text-[#F9F6F0] hover:bg-[#2A2421]/90 rounded-full"
                data-testid="save-testimonial"
              >
                <Save size={14} className="me-1.5" />
                {t.common.save}
              </Button>
              <Button
                onClick={cancel}
                variant="ghost"
                className="rounded-full"
                data-testid="cancel-testimonial"
              >
                <X size={14} className="me-1.5" />
                {t.common.cancel}
              </Button>
            </div>
          </div>
        )}

        {listQuery.isLoading ? (
          <p className="text-[#5C544F]">{t.common.loading}</p>
        ) : items.length === 0 ? (
          <p className="text-[#5C544F]">{t.testimonials.empty}</p>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="rounded-2xl bg-white border border-[#2A2421]/10 p-5 flex items-start gap-4"
                data-testid={`admin-row-${item.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-[#2A2421]">
                      {item.name}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#2A2421]/8 text-[#5C544F]">
                      {item.lang}
                    </span>
                    <div className="flex items-center text-[#C86240]">
                      {Array.from({ length: item.rating || 0 }).map((_, i) => (
                        <Star key={i} size={11} className="fill-[#C86240]" />
                      ))}
                    </div>
                  </div>
                  {item.cafe && (
                    <div className="text-xs text-[#5C544F] mb-1.5">
                      {item.cafe}
                    </div>
                  )}
                  <p
                    className="text-sm text-[#2A2421]/85 line-clamp-2"
                    dir={item.lang === "ar" ? "rtl" : "ltr"}
                  >
                    {item.text}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => startEdit(item)}
                    className="p-2 rounded-lg hover:bg-[#2A2421]/5 text-[#5C544F]"
                    aria-label="edit"
                    data-testid={`edit-${item.id}`}
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => remove(item.id)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                    aria-label="delete"
                    data-testid={`delete-${item.id}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
